/**
 * Ingest export — three-level drill-down (build plan §1.6, Segment 2).
 *
 * Level 1 — "Which project?": project folders sorted by number.
 * Level 2 — inside one project: that project's subjects + add-new inline.
 * Level 3 — one subject: make export versions (Verify files the export).
 *
 * Every screen answers ONE question (§1.6). Drill-down like folders.
 * Back buttons name where they go. Breadcrumb shows the full trail.
 *
 * Segment 4 — the bridge reads the file: Verify without specs files the export
 * and asks a studio computer's NYC Bridge (verify capability, its clients root
 * confirmed) to read it under C:\Clients\{job}\Exports\{display folder}\; the
 * row then says "verified by <machine>". Specs typed by hand are the fallback
 * (no bridge, an older bridge, a file it cannot read) and say "entered by hand".
 */
import React, { useEffect, useState } from "react";
import { useFirestoreCollection } from "../../../../lib/useFirestore.js";
import { handheld, newClientTempId } from "../../../../lib/handheldCallables.js";
import { undoNewSubjectInProject } from "../../../../lib/handheldRollback.js";
import {
  pad3, attemptExportFilename, latestAttempt, exportsFolderPath, projectFileName, TETHERED,
} from "../../../../lib/handheldNaming.js";
import { groupSubjectsByProject, defaultToolForSoftware } from "../../../../lib/handheldExportHelpers.js";
import { nameTaken, CategorySelect } from "./subjectForm.jsx";

const NO_PROJECT = "__no_project__";
const pptCode = pad3;
const NO_SERIAL_YET = "(waiting for this attempt's serial number)";

// ── Shared small components ────────────────────────────────────────────────────

function copyText(t, onDone) {
  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(t).then(onDone).catch(onDone);
  else onDone();
}

function CopyBtn({ text, label = "Copy" }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" className={`proc-copy ${done ? "done" : ""}`}
      onClick={() => copyText(text, () => { setDone(true); setTimeout(() => setDone(false), 1100); })}>
      {done ? "✓ copied" : label}
    </button>
  );
}

function MiniThumb({ subject }) {
  if (!subject.referencePhoto) return <div className="hh-thumb hh-thumb-add proc-thumb"><span>no photo</span></div>;
  return (
    <div className={`hh-thumb v${subject.number % 3} proc-thumb`} aria-hidden="true">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="8.5" cy="9.5" r="1.6" /><path d="M21 16l-5-5L5 20" /></svg>
    </div>
  );
}

// ── Export naming helpers (ported from the mockup) ──────────────────────────────
const TYPE_FOR_CODE = { leo: "3D model", sps: "3D model", nmb: "3D model", pht: "3D model", rtc: "Point cloud", blk: "Point cloud", xgd: "Splat" };
const EXPORT_TYPES = ["3D model", "Point cloud", "Splat"];
let _seq = 0;

function combinedCode(tools) {
  const others = tools.filter((t) => t !== "pht");
  const hasPht = tools.includes("pht");
  return [...others, ...(hasPht ? ["pht"] : [])].join("-") || (tools[0] ?? "pht");
}

function formatPoly(raw) {
  const n = parseInt(String(raw ?? "").replace(/[^0-9]/g, ""), 10);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n >= 1_000_000) { const m = n / 1_000_000; return `${Number.isInteger(m) ? m : Math.round(m * 10) / 10}m`; }
  return `${Math.round(n / 1000)}k`;
}

function newDraft(tools) {
  const single = tools[0] ?? "pht";
  return {
    id: `d${_seq += 1}`, mode: "single", single, combined: [...tools],
    type: TYPE_FOR_CODE[single] ?? "3D model", polyMode: "num", polyNum: "", variant: null,
    sizeNum: "", sizeUnit: "MB", format: "", texFormat: "", texSize: "", texCount: "",
  };
}
const draftCode = (d) => (d.mode === "single" ? d.single : combinedCode(d.combined));
const polySuffix = (d) => (d.type !== "3D model" ? null : (d.polyMode === "num" ? formatPoly(d.polyNum) : d.polyMode));
const draftSuffix = (d) => [d.mode === "single" ? (d.variant || null) : null, polySuffix(d)].filter(Boolean).join("_") || null;
const draftValid = (d) => {
  if (d.mode === "combined" && d.combined.length < 2) return false;
  if (d.type === "3D model" && d.polyMode === "num" && !formatPoly(d.polyNum) && !d.variant) return false;
  return true;
};

const suggestedFormat = (d) =>
  d.type === "Splat" ? "PLY" : d.type === "Point cloud" ? "E57" : (d.polyMode === "preview" ? "GLB" : "OBJ");

function buildMeta(d) {
  const n = parseFloat(String(d.sizeNum).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(n) || n <= 0) return undefined;
  const bytes = Math.round(n * (d.sizeUnit === "GB" ? 1073741824 : 1048576));
  return {
    fileSize: bytes,
    format: (d.format || "").trim() || suggestedFormat(d),
    textureFormat: (d.texFormat || "").trim() || null,
    textureSize: (d.texSize || "").trim() || null,
    textureCount: parseInt(d.texCount, 10) || 0,
  };
}

function formatBytes(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "—";
  const mb = bytes / 1048576;
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}

function MetaRow({ meta }) {
  if (!meta) return null;
  return (
    <div className="exp-meta">
      <span className="exp-meta-item"><span className="exp-meta-k">size</span> {formatBytes(meta.fileSize)}</span>
      {meta.format && <span className="exp-meta-item"><span className="exp-meta-k">format</span> {meta.format}</span>}
      {meta.textureFormat && <span className="exp-meta-item"><span className="exp-meta-k">texture</span> {meta.textureFormat}{meta.textureSize ? ` · ${meta.textureSize}` : ""}</span>}
      {meta.textureCount > 0 && <span className="exp-meta-item"><span className="exp-meta-k">maps</span> {meta.textureCount}</span>}
    </div>
  );
}

// ── Segment 4: who verified an export's specs ──────────────────────────────────

const MISS_WORDS = {
  not_found: "not found there",
  ambiguous: "found in two display-name folders",
  unsupported: "no model file it can read",
  unreadable: "could not read the file",
  too_large: "too big to read",
  timeout: "took too long",
  outside_root: "a link leads outside the clients folder",
};

/**
 * Where an export's specs stand (pure, for the screen and its tests):
 * machine (a bridge read the file), manual (typed by hand), reading (a read
 * is open), unread (nobody read it: expired, cancelled, or never asked).
 */
export function exportVerifyStatus(ex, now = Date.now()) {
  const v = ex?.verify;
  // An open read comes first, even over specs already there (Verify again after a read or a typed entry).
  if (v?.state === "pending" && v.expiresAt > now) return { kind: "reading", again: !!ex?.meta, misses: v.misses ?? [] };
  // The latest read ran out with no answer, after the specs shown: say so (the specs stay, marked older).
  const lastReadFailed = v && (v.state === "expired" || v.state === "pending") && (!ex?.verifiedAt || v.requestedAt > ex.verifiedAt);
  if (lastReadFailed) return { kind: "unread", stale: !!ex?.meta, misses: v.misses ?? [] };
  if (ex?.meta && ex.metaSource === "machine") {
    return { kind: "machine", label: ex.verifiedBy?.label || "a studio computer" };
  }
  if (ex?.meta) return { kind: "manual" }; // typed, or specs from before Segment 4 (which were typed)
  return { kind: "unread", misses: v?.misses ?? [] };
}

function ExportStatus({ jobId, subjectId, ex }) {
  const [now, setNow] = useState(() => Date.now());
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const status = exportVerifyStatus(ex, now);
  const reading = status.kind === "reading";
  useEffect(() => {
    if (!reading) return undefined;
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [reading]);

  const again = async () => {
    setBusy(true); setErr(null);
    try { await handheld.requestExportVerify({ jobId, subjectId, exportId: ex.id }); setNow(Date.now()); }
    catch (e) { setErr(e?.message ?? "Could not ask again"); }
    setBusy(false);
  };
  const save = async () => {
    const meta = buildMeta(form);
    if (!meta) { setErr("Enter the size"); return; }
    setBusy(true); setErr(null);
    try { await handheld.enterExportSpecs({ jobId, subjectId, exportId: ex.id, meta }); setForm(null); }
    catch (e) { setErr(e?.message ?? "Could not save the specs"); }
    setBusy(false);
  };

  return (
    <>
      <div className="exp-status">
        {status.kind === "machine" && <span className="proc-verified">✓ verified by {status.label}</span>}
        {status.kind === "manual" && <span className="proc-verified manual">✓ entered by hand</span>}
        {status.kind === "reading" && (
          <span className="exp-reading">{status.again ? "reading the file again on a studio computer…" : "reading the file on a studio computer…"}</span>
        )}
        {(status.kind === "machine" || status.kind === "manual") && !form && (
          <button type="button" className="raw-act" disabled={busy} onClick={again}>Verify again</button>
        )}
        {status.kind === "unread" && (
          <span className="exp-unread">
            {status.stale ? "no studio computer read this file on the last try (the specs below are older)" : "no studio computer read this file"}
          </span>
        )}
        {(status.kind === "reading" || status.kind === "unread") && !form && (
          <>
            {status.kind === "unread" && (
              <button type="button" className="raw-act" disabled={busy} onClick={again}>Verify again</button>
            )}
            <button type="button" className="raw-act" disabled={busy}
              onClick={() => setForm({ sizeNum: "", sizeUnit: "MB", format: "", texFormat: "", texSize: "", texCount: "", type: "3D model", polyMode: "num" })}>
              Enter by hand
            </button>
          </>
        )}
      </div>
      {(status.misses ?? []).length > 0 && status.kind !== "machine" && (
        <div className="exp-misses">
          {status.misses.map((m) => (
            <span key={m.machineUid} className="exp-miss">{m.label}: {MISS_WORDS[m.reason] ?? m.reason}</span>
          ))}
        </div>
      )}
      <MetaRow meta={ex.meta} />
      {form && (
        <div className="exp-specs">
          <span className="rw-toolslabel">Specs</span>
          <input className="exp-polynum mono" placeholder="size" value={form.sizeNum} onChange={(e) => setForm({ ...form, sizeNum: e.target.value })} />
          <select className="hh-code-sel mono exp-unit" value={form.sizeUnit} onChange={(e) => setForm({ ...form, sizeUnit: e.target.value })}>
            <option value="MB">MB</option>
            <option value="GB">GB</option>
          </select>
          <input className="exp-polynum mono" placeholder="OBJ" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} />
          <input className="exp-polynum mono" placeholder="texture (PNG)" value={form.texFormat} onChange={(e) => setForm({ ...form, texFormat: e.target.value })} />
          <input className="exp-polynum mono" placeholder="4096×4096" value={form.texSize} onChange={(e) => setForm({ ...form, texSize: e.target.value })} />
          <input className="exp-polynum mono exp-maps" placeholder="# maps" value={form.texCount} onChange={(e) => setForm({ ...form, texCount: e.target.value })} />
          <button type="button" className="hh-btn primary" disabled={busy} onClick={save}>{busy ? "saving…" : "Save specs"}</button>
          <button type="button" className="hh-btn ghost" disabled={busy} onClick={() => { setForm(null); setErr(null); }}>Cancel</button>
        </div>
      )}
      {err && <span className="hh-name-err">{err}</span>}
    </>
  );
}

// ── Level 1 — "Which project?" ─────────────────────────────────────────────────

function ProjectPicker({ jobLabel, subjects, projects, onPickProject, onDone }) {
  const { projects: projectGroups, noProject } = groupSubjectsByProject(subjects, projects);
  return (
    <div className="raw-flow">
      <button type="button" className="ingest-back" onClick={onDone}>← {jobLabel} · job home</button>
      <div className="raw-title">Ingest export</div>
      {projectGroups.map(({ project, subjects: subs, exportCount }) => (
        <button key={project.id} type="button" className="exp-folder" onClick={() => onPickProject(project.id)}>
          <span className="exp-ptag">Project</span>
          <span className="exp-folder-info">
            <span className="exp-fname">{project.software}</span>
            <span className="exp-ffile mono">file: {projectFileName(jobLabel, project, subjects, projects)}</span>
          </span>
          <span className="exp-fmeta">
            <span>{subs.length} subject{subs.length !== 1 ? "s" : ""}</span>
            {exportCount > 0
              ? <span className="done">{exportCount} export{exportCount !== 1 ? "s" : ""} done</span>
              : <span>no exports yet</span>}
          </span>
          <span className="exp-chev" aria-hidden="true">›</span>
        </button>
      ))}
      {noProject.subjects.length > 0 && (
        <button type="button" className="exp-folder" onClick={() => onPickProject(NO_PROJECT)}>
          <span className="exp-ptag none">No project</span>
          <span className="exp-folder-info">
            <span className="exp-fname">Ungrouped subjects</span>
            <span className="exp-ffile">point clouds &amp; direct exports</span>
          </span>
          <span className="exp-fmeta">
            <span>{noProject.subjects.length} subject{noProject.subjects.length !== 1 ? "s" : ""}</span>
            {noProject.exportCount > 0 && (
              <span className="done">{noProject.exportCount} export{noProject.exportCount !== 1 ? "s" : ""} done</span>
            )}
          </span>
          <span className="exp-chev" aria-hidden="true">›</span>
        </button>
      )}
      {projectGroups.length === 0 && noProject.subjects.length === 0 && (
        <div className="hh-empty">No projects or ungrouped subjects yet.</div>
      )}
    </div>
  );
}

// ── Level 2 — inside one project: "Which subject?" ─────────────────────────────

function ProjectSubjects({ jobId, jobLabel, project, subjects, projects, isNoProject, canErase = false, onPickSubject, onBack, onDone }) {
  const defTool = isNoProject ? TETHERED[0].code : defaultToolForSoftware(project?.software, TETHERED);
  const [adding, setAdding] = useState(false);
  const [addTool, setAddTool] = useState(defTool);
  const [addName, setAddName] = useState("");
  const [addCat, setAddCat] = useState("Object");
  const [addErr, setAddErr] = useState(null);
  const [addBusy, setAddBusy] = useState(false);

  const projSubjects = isNoProject
    ? groupSubjectsByProject(subjects, []).noProject.subjects
    : subjects.filter((s) => (s.projectIds ?? []).includes(project.id)).sort((a, b) => (a.number ?? 0) - (b.number ?? 0));

  const pFileName = !isNoProject ? projectFileName(jobLabel, project, subjects, projects) : null;
  const headerLabel = isNoProject ? "No project" : `${project.software} · file: ${pFileName}`;

  const createSubject = async () => {
    const name = addName.trim();
    if (!name || addBusy) return;
    if (nameTaken(subjects, name)) { setAddErr("Name already used on this job"); return; }
    setAddBusy(true);
    setAddErr(null);
    let newId = null;
    let newNumber = null;
    try {
      const res = await handheld.addSubject({
        jobId, displayName: name, category: addCat,
        capturedBy: [addTool], clientTempId: newClientTempId(),
      });
      newId = res.id;
      newNumber = res.number;
      await handheld.linkSubjectToProject({ jobId, subjectId: newId, projectId: project.id });
      onPickSubject(newId);
    } catch (e) {
      // Undo the link (its answer may be lost); erasing the new subject is an
      // admin's (Segment 5: it uses up the number). handheldRollback.js.
      const left = newId
        ? await undoNewSubjectInProject(handheld, { jobId, projectId: project.id, subject: { id: newId, number: newNumber }, canErase })
        : null;
      setAddErr([e?.message ?? "Could not create subject", left].filter(Boolean).join(" "));
      setAddBusy(false);
    }
  };

  return (
    <div className="raw-flow">
      <button type="button" className="ingest-back" onClick={onBack}>← All projects</button>
      <div className="exp-crumb">
        {/* crumbs are doors, not just signs (Finder model, Ivin 2026-06-12) */}
        <button type="button" className="exp-crumb-link" onClick={onDone}>{jobLabel}</button>
        <span className="exp-sep">›</span>
        <span className="exp-here">{headerLabel}</span>
      </div>
      {!isNoProject && (
        <div className="exp-phead">
          <span className="exp-ptag">Project</span>
          <span className="exp-pname">{project.software}</span>
          <span className="exp-pfile mono">file: {pFileName}</span>
        </div>
      )}
      {projSubjects.map((s) => {
        const n = s.exports?.length ?? 0;
        return (
          <button key={s.id} type="button" className="exp-srow" onClick={() => onPickSubject(s.id)}>
            <span className="hh-ppt-num mono">{pptCode(s.number)}</span>
            <span className="proc-pname">{s.displayName}</span>
            <span className="exp-stools mono">{(s.capturedBy ?? []).join(" · ") || "—"}</span>
            <span className={`exp-ecount ${n ? "has" : "none"}`}>
              {n ? `${n} export${n !== 1 ? "s" : ""} ›` : "no exports yet ›"}
            </span>
          </button>
        );
      })}
      {projSubjects.length === 0 && (
        <div className="hh-empty">No subjects in this {isNoProject ? "group" : "project"} yet.</div>
      )}
      {!isNoProject && !adding && (
        <button type="button" className="hh-addsubj" onClick={() => setAdding(true)}>+ new subject in this project</button>
      )}
      {!isNoProject && adding && (
        <div className="exp-addform">
          <select className="hh-cat" value={addTool} onChange={(e) => setAddTool(e.target.value)}>
            {TETHERED.map((g) => <option key={g.code} value={g.code}>{g.code}</option>)}
          </select>
          <input
            className="hh-name-input"
            placeholder="subject name"
            value={addName}
            autoFocus
            disabled={addBusy}
            onChange={(e) => { setAddName(e.target.value); setAddErr(nameTaken(subjects, e.target.value) ? "Name already used on this job" : null); }}
            onKeyDown={(e) => { if (e.key === "Enter") createSubject(); if (e.key === "Escape") setAdding(false); }}
          />
          <CategorySelect value={addCat} onChange={setAddCat} />
          <button type="button" className="hh-btn primary" disabled={!addName.trim() || !!addErr || addBusy} onClick={createSubject}>
            {addBusy ? "Creating…" : "Create → export versions"}
          </button>
          <button type="button" className="hh-btn ghost" disabled={addBusy} onClick={() => setAdding(false)}>Cancel</button>
          {addErr && <span className="hh-name-err">{addErr}</span>}
        </div>
      )}
    </div>
  );
}

// ── Level 3 — one subject's export versions ────────────────────────────────────

function SubjectExports({ jobId, jobLabel, subject, project, subjects, projects, isNoProject, onBack, onDone }) {
  const tools = subject.capturedBy?.length ? subject.capturedBy : ["pht"];
  const ingested = subject.exports ?? [];
  const [drafts, setDrafts] = useState(() => [newDraft(tools)]);
  const [busy, setBusy] = useState({});
  const [err, setErr] = useState({});
  const nameStem = jobLabel;
  const location = exportsFolderPath(nameStem, subject.displayName);
  const attempt = latestAttempt(subject); // a new export goes to the subject's latest attempt

  const pFileName = !isNoProject && project ? projectFileName(jobLabel, project, subjects, projects) : null;
  const backLabel = isNoProject ? "No project" : `${project?.software} · ${pFileName}`;
  const crumbPFile = pFileName ? pFileName.replace(jobLabel, "…") : null;
  const crumbProject = isNoProject ? "No project" : `${project?.software} · ${crumbPFile}`;

  const variantsForCode = (code) => (subject.rawSets ?? []).filter((r) => r.code === code && r.suffix).map((r) => r.suffix);

  const setDraft = (id, patch) => setDrafts((ds) => ds.map((d) => (d.id === id ? { ...d, ...patch } : d)));
  const addDraft = () => setDrafts((ds) => [...ds, newDraft(tools)]);
  const removeDraft = (id) => setDrafts((ds) => ds.filter((d) => d.id !== id));
  const toggleCombinedTool = (id, code) => setDrafts((ds) => ds.map((d) => {
    if (d.id !== id) return d;
    const has = d.combined.includes(code);
    return { ...d, combined: has ? d.combined.filter((c) => c !== code) : [...d.combined, code] };
  }));

  const verify = async (d) => {
    setBusy((b) => ({ ...b, [d.id]: true }));
    setErr((e) => { const n = { ...e }; delete n[d.id]; return n; });
    const lineage = d.mode === "combined" ? d.combined.map((c) => `${c}_${pptCode(subject.number)}`) : null;
    const meta = buildMeta(d);
    try {
      // Filed to the attempt whose name the draft shows (not whichever is latest when the call lands).
      await handheld.attachExport({ jobId, subjectId: subject.id, code: draftCode(d), suffix: draftSuffix(d), lineage, meta, attempt });
      removeDraft(d.id);
    } catch (e) {
      setErr((er) => ({ ...er, [d.id]: e?.message ?? "Could not file export" }));
      setBusy((b) => { const n = { ...b }; delete n[d.id]; return n; });
    }
  };

  return (
    <div className="raw-flow">
      <button type="button" className="ingest-back" onClick={onBack}>← {backLabel}</button>
      <div className="exp-crumb">
        {/* crumbs are doors, not just signs (Finder model, Ivin 2026-06-12) */}
        <button type="button" className="exp-crumb-link" onClick={onDone}>{jobLabel}</button>
        <span className="exp-sep">›</span>
        <button type="button" className="exp-crumb-link" onClick={onBack}>{crumbProject}</button>
        <span className="exp-sep">›</span>
        <span className="exp-here">{pptCode(subject.number)} · {subject.displayName}</span>
      </div>

      <div className="exp-subjhead">
        <MiniThumb subject={subject} />
        <div>
          <div>
            <span className="hh-ppt-num mono">{pptCode(subject.number)}</span>{" "}
            <span className="exp-subjname">{subject.displayName}</span>
          </div>
          <div className="exp-subjtools mono">{tools.join(" · ")}</div>
        </div>
      </div>

      {ingested.length > 0 && (
        <>
          <div className="exp-sect">Exported versions — done</div>
          <div className="proc-step">
            {ingested.map((ex) => (
              <div key={ex.id} className="exp-done">
                <div className="exp-done-top">
                  <span className={`mono proc-stem ${ex.lineage ? "fused" : ""}`}>
                    {attemptExportFilename(nameStem, ex.code, subject.number, ex.suffix, ex.attempt ?? 1, subject.attempts) ?? NO_SERIAL_YET}
                  </span>
                </div>
                <ExportStatus jobId={jobId} subjectId={subject.id} ex={ex} />
              </div>
            ))}
          </div>
        </>
      )}

      <div className="exp-sect">{ingested.length ? "Add a version" : "Export a version"}</div>
      <div className="proc-step">
        {drafts.map((d) => {
          // A retake's name carries its own serial number.
          const name = attemptExportFilename(nameStem, draftCode(d), subject.number, draftSuffix(d), attempt, subject.attempts) ?? NO_SERIAL_YET;
          return (
            <div key={d.id} className="exp-draft">
              <div className="exp-row">
                {tools.length > 1 && (
                  <div className="raw-modes">
                    <button type="button" className={`raw-mode ${d.mode === "single" ? "on" : ""}`} onClick={() => setDraft(d.id, { mode: "single" })}>One tool</button>
                    <button type="button" className={`raw-mode ${d.mode === "combined" ? "on" : ""}`} onClick={() => setDraft(d.id, { mode: "combined" })}>Combined</button>
                  </div>
                )}
                {d.mode === "single" ? (
                  tools.length > 1 ? (
                    <select className="hh-code-sel mono" value={d.single}
                      onChange={(e) => setDraft(d.id, { single: e.target.value, type: TYPE_FOR_CODE[e.target.value] ?? d.type, variant: null })}>
                      {tools.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : <span className="hh-code mono">{tools[0]}</span>
                ) : (
                  <span className="exp-combochips">
                    {tools.map((t) => (
                      <button type="button" key={t} className={`rw-tool mono ${d.combined.includes(t) ? "on" : ""}`} onClick={() => toggleCombinedTool(d.id, t)}>{t}</button>
                    ))}
                  </span>
                )}
                <select className="hh-cat" value={d.type} onChange={(e) => setDraft(d.id, { type: e.target.value })}>
                  {EXPORT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>

              {d.mode === "single" && variantsForCode(d.single).length > 0 && (
                <div className="exp-row">
                  <span className="rw-toolslabel">Camera</span>
                  <select className="hh-code-sel mono" value={d.variant ?? ""} onChange={(e) => setDraft(d.id, { variant: e.target.value || null })}>
                    <option value="">—</option>
                    {variantsForCode(d.single).map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
              )}

              {d.type === "3D model" ? (
                <div className="exp-row exp-poly">
                  <span className="rw-toolslabel">Poly</span>
                  <button type="button" className={`exp-polytab ${d.polyMode === "num" ? "on" : ""}`} onClick={() => setDraft(d.id, { polyMode: "num" })}>Count</button>
                  {d.polyMode === "num" && (
                    <input className="exp-polynum mono" placeholder="400000" value={d.polyNum} onChange={(e) => setDraft(d.id, { polyNum: e.target.value })} />
                  )}
                  <button type="button" className={`exp-polytab ${d.polyMode === "high" ? "on" : ""}`} onClick={() => setDraft(d.id, { polyMode: "high" })}>High</button>
                  <button type="button" className={`exp-polytab ${d.polyMode === "preview" ? "on" : ""}`} onClick={() => setDraft(d.id, { polyMode: "preview" })}>Preview</button>
                </div>
              ) : (
                <div className="exp-row exp-poly"><span className="raw-hint">{d.type} — one version, no polycount</span></div>
              )}

              <div className="exp-specs">
                <span className="rw-toolslabel">Specs</span>
                <input className="exp-polynum mono" placeholder="size" value={d.sizeNum} onChange={(e) => setDraft(d.id, { sizeNum: e.target.value })} />
                <select className="hh-code-sel mono exp-unit" value={d.sizeUnit} onChange={(e) => setDraft(d.id, { sizeUnit: e.target.value })}>
                  <option value="MB">MB</option>
                  <option value="GB">GB</option>
                </select>
                <input className="exp-polynum mono" placeholder={suggestedFormat(d)} value={d.format} onChange={(e) => setDraft(d.id, { format: e.target.value })} />
                <input className="exp-polynum mono" placeholder="texture (PNG)" value={d.texFormat} onChange={(e) => setDraft(d.id, { texFormat: e.target.value })} />
                <input className="exp-polynum mono" placeholder="4096×4096" value={d.texSize} onChange={(e) => setDraft(d.id, { texSize: e.target.value })} />
                <input className="exp-polynum mono exp-maps" placeholder="# maps" value={d.texCount} onChange={(e) => setDraft(d.id, { texCount: e.target.value })} />
              </div>
              <div className="exp-specs-hint">leave the specs empty and a studio computer reads them from the file; type them only if no bridge can</div>

              <div className="ip-saveas exp-saveas">
                <div className="proc-pathrow">
                  <span className="sa-key">Save as</span>
                  <span className={`mono proc-path ${d.mode === "combined" ? "fused" : ""}`}>{name}</span>
                  <CopyBtn text={name} />
                </div>
                <div className="proc-pathrow">
                  <span className="sa-key">In</span>
                  <span className="mono proc-path">{location}</span>
                  <CopyBtn text={location} />
                  <button type="button" className={`proc-verify ${busy[d.id] ? "checking" : ""}`} disabled={!draftValid(d) || busy[d.id]} onClick={() => verify(d)}>
                    {busy[d.id] ? "filing…" : "Verify"}
                  </button>
                </div>
              </div>

              {err[d.id] && <span className="hh-name-err">{err[d.id]}</span>}
              {drafts.length > 1 && <button type="button" className="raw-act danger exp-remove" onClick={() => removeDraft(d.id)}>Remove</button>}
            </div>
          );
        })}
        <button type="button" className="hh-btn ghost exp-add" onClick={addDraft}>+ add a version</button>
      </div>

      {/* two "finished" moments: back = next subject (1 tap), Done = job home (1 tap) */}
      <div className="raw-foot">
        <button type="button" className="hh-btn primary" onClick={onDone}>Done</button>
      </div>
    </div>
  );
}

// ── Ingest export — three-level drill-down ──────────────────────────────────────

export default function IngestExport({ jobId, jobLabel, canErase = false, onDone }) {
  const { data: subjects } = useFirestoreCollection(`jobs/${jobId}/subjects`);
  const { data: projects } = useFirestoreCollection(`jobs/${jobId}/projects`);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const nameStem = jobLabel ?? jobId;

  // The picked project vanished mid-session (deleted on another device) —
  // degrade to a named way back instead of crashing on project.id below
  // (Codex slice-3 finding). Rare: deletion is empty-only/rollback.
  if (selectedProject !== null && selectedProject !== NO_PROJECT && !projects.find((p) => p.id === selectedProject)) {
    return (
      <div className="raw-flow">
        <button type="button" className="ingest-back" onClick={() => { setSelectedSubject(null); setSelectedProject(null); }}>← All projects</button>
        <div className="hh-empty">Project no longer available.</div>
      </div>
    );
  }

  // Level 3: one subject's export versions
  if (selectedProject !== null && selectedSubject !== null) {
    const subject = subjects.find((s) => s.id === selectedSubject);
    const project = selectedProject !== NO_PROJECT
      ? projects.find((p) => p.id === selectedProject) : null;
    if (!subject) return <div className="raw-flow"><div className="hh-empty">Loading subject…</div></div>;
    return (
      <SubjectExports
        jobId={jobId}
        jobLabel={nameStem}
        subject={subject}
        project={project}
        subjects={subjects}
        projects={projects}
        isNoProject={selectedProject === NO_PROJECT}
        onBack={() => setSelectedSubject(null)}
        onDone={onDone}
      />
    );
  }

  // Level 2: subjects inside one project
  if (selectedProject !== null) {
    const project = selectedProject !== NO_PROJECT
      ? projects.find((p) => p.id === selectedProject) : null;
    return (
      <ProjectSubjects
        jobId={jobId}
        jobLabel={nameStem}
        project={project}
        subjects={subjects}
        projects={projects}
        isNoProject={selectedProject === NO_PROJECT}
        canErase={canErase}
        onPickSubject={setSelectedSubject}
        onBack={() => setSelectedProject(null)}
        onDone={onDone}
      />
    );
  }

  // Level 1: pick a project
  return (
    <ProjectPicker
      jobLabel={nameStem}
      subjects={subjects}
      projects={projects}
      onPickProject={setSelectedProject}
      onDone={onDone}
    />
  );
}

// Named exports for tests
export { ProjectPicker, ProjectSubjects, SubjectExports, ExportStatus };
